import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  ArrowUpRight,
  Mail,
  Send,
  CheckCircle2,
  BookOpen,
  MessagesSquare,
  Loader2,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from '../i18n/Link'
import SEO, { schemas } from '../components/SEO'
import { contactApi } from '../lib/api'
import { PageHeading } from '../components/Interior'
const resourceLinks = [
  { icon: BookOpen, path: '/docs' },
  { icon: MessagesSquare, path: '/community' },
]
export default function Contact() {
  const { t } = useTranslation(['contact', 'common'])
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  })
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>(
    'idle',
  )
  const resources = t('resources', { returnObjects: true }) as {
    title: string
    text: string
  }[]
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (state === 'sending') return
    setState('sending')
    try {
      await contactApi.send(form)
      setState('sent')
      setForm({ name: '', email: '', subject: '', message: '' })
    } catch {
      setState('error')
    }
  }
  return (
    <div className="designed-page contact-page">
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/contact"
        structuredData={[schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.contact'), url: '/contact' }])]}
      />
      <PageHeading
        label={t('heading.label')}
        title={t('heading.title')}
        accent={t('heading.accent')}
        description={t('heading.description')}
      >
        <div className="contact-postcard" aria-hidden="true">
          <span>{t('postcard.greeting')}</span>
          <img src="/brand/mark.svg" width="130" height="96" alt="" />
          <div>
            {t('postcard.line1')}
            <br />
            <strong>{t('postcard.line2')}</strong>
          </div>
          <Mail size={30} />
        </div>
      </PageHeading>
      <div className="contact-workbench">
        <aside className="contact-address">
          <span className="eyebrow">{t('address.eyebrow')}</span>
          <h2>
            {t('address.title')}
            <br />
            {t('address.titleLine2')}
          </h2>
          <p>{t('address.text')}</p>
          <a className="contact-email" href="mailto:destek@cmdesk.help">
            <Mail size={21} />
            <span>
              {t('address.emailLabel')}<strong>destek@cmdesk.help</strong>
            </span>
            <ArrowUpRight size={18} />
          </a>
          <div className="contact-side-note">
            <span>{t('address.noteTitle')}</span>
            <p>{t('address.noteText')}</p>
          </div>
        </aside>
        <section className="letter-form">
          <div className="letter-heading">
            <span>{t('form.eyebrow')}</span>
            <Send size={20} />
          </div>
          {state === 'sent' ? (
            <div className="contact-success" role="status">
              <CheckCircle2 size={48} />
              <h2>{t('success.title')}</h2>
              <p>{t('success.text')}</p>
              <button
                className="outline-button"
                onClick={() => setState('idle')}
              >
                {t('success.again')}
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h2>{t('form.title')}</h2>
              <div className="form-two">
                <label htmlFor="contact-name">
                  {t('form.name')}
                  <input
                    id="contact-name"
                    autoComplete="name"
                    required
                    maxLength={100}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('form.namePlaceholder')}
                  />
                </label>
                <label htmlFor="contact-email">
                  {t('form.email')}
                  <input
                    id="contact-email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    placeholder={t('form.emailPlaceholder')}
                  />
                </label>
              </div>
              <label htmlFor="contact-subject">
                {t('form.subject')}
                <input
                  id="contact-subject"
                  required
                  maxLength={200}
                  value={form.subject}
                  onChange={(e) =>
                    setForm({ ...form, subject: e.target.value })
                  }
                  placeholder={t('form.subjectPlaceholder')}
                />
              </label>
              <label htmlFor="contact-message">
                {t('form.message')}
                <textarea
                  id="contact-message"
                  required
                  rows={5}
                  maxLength={5000}
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                  placeholder={t('form.messagePlaceholder')}
                />
              </label>
              {state === 'error' && (
                <p className="form-error" role="alert">
                  {t('form.error')}
                </p>
              )}
              <div className="form-bottom">
                <p>
                  {t('form.privacyBefore')}
                  <br />
                  <Link to="/privacy">{t('form.privacyLink')}</Link>{' '}
                  {t('form.privacyAfter')}
                </p>
                <button
                  className="jade-button"
                  disabled={state === 'sending'}
                  type="submit"
                >
                  {state === 'sending' ? (
                    <Loader2 size={17} className="animate-spin" />
                  ) : (
                    <Send size={17} />
                  )}
                  {state === 'sending' ? t('form.sending') : t('form.submit')}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
      <div className="contact-resources">
        {resourceLinks.map(({ icon: Icon, path }, index) => (
          <Link to={path} key={path}>
            <Icon size={25} />
            <div>
              <h3>{resources[index]?.title}</h3>
              <p>{resources[index]?.text}</p>
            </div>
            <ArrowUpRight size={20} />
          </Link>
        ))}
      </div>
    </div>
  )
}
